import React from "react";
import {
  NAME_LABEL,
  SURNAME_LABEL,
  PRACTICE_LABEL,
  CC_LABEL,
  FORM_STATUS_CM_LABEL,
  TYPOLOGY_LABEL,
  DURATION_LABEL,
  CATEGORY_LABEL,
  STATE_LABEL,
  DATE_ACHIEVEMENT_LABEL,
  VALIDITY_EXPIRATION_LABEL,
  EMPTY_STRING,
  PERFORMANCE_COACH_LABEL,
  STARTING_DATE_LABEL,
  ENDING_DATE_LABEL,
  ATTENDANCE_YEAR_LABEL,
  ROLE_LABEL,
  COMPLETED_LABEL,
  IN_PROGRESS_LABEL,
  VALIDITY_LABEL,
  INTERNAL_GRADE_LABEL,
  SCOPE_LABEL,
  CORE_COMPETENCE_LABEL,
  SUB_COMPETENCE_LABEL,
  LISTENING_LABEL,
  READING_LABEL,
  INTERACTION_LABEL,
  SPOKEN_PRODUCTION,
  SPOKEN_LABEL,
  VERIFICATION_STATUS_LABEL,
  WRITTEN_LABEL,
  COMPREHENSION_LABEL,
  CM_SHEET_LABEL,
  CONTRACT_TYPE,
  EMAIL_LABEL,
  PC_ABBREVIATION_LABEL,
  FORM_STATUS_LABEL,
  LEVEL_LABEL,
  COMPANY_NAME_LABEL,
  EDUCATIONAL_LABEL,
  STUDY_PATH_LABEL,
  YEAR_START_LABEL,
  YEAR_END_LABEL,
  CORPORATION_LABEL,
  LANGUAGE_LABEL,
  EDUCATIONAL_QUALIFICATION_LABEL,
} from "./Dictionary";
import { ReactComponent as CertificateIcon } from "../icons/certificate.svg";
import { ReactComponent as UploadIcon } from "../icons/upload.svg";
import { ReactComponent as CheckIcon } from "../icons/check.svg";
import { ReactComponent as CloseIcon } from "../icons/close.svg";
import { Avatar, AvatarDimensionsEnum } from "../components/core/Avatar/Avatar";
import { FontWeightEnum, IconSizeEnum } from "../types/AppTypes";
import {
  CollaboratorCM,
  Collaborator,
  CollaboratorHistoryCourse,
  CollaboratorHistoryCertification,
  CollaboratorCmSheetCourse,
  CollaboratorCmSheetCertification,
  CollaboratorsHistorySkillLanguages,
  CollaboratorsHistoryTechSkill,
  CollaboratorHistoryProfessionalExperience,
  CollaboratorHistoryEducation,
} from "../types/api/collaborators.type";
import { IconManager } from "../components/core/IconManager/IconManager";
import { Course } from "../types/api/courses.type";
import ToggleSwitch from "../components/core/ToggleSwitch/ToggleSwitch";
import { Certification } from "../types/api/certifications.type";
import {
  CollaboratorTypeEnum,
  MANAGE_STATUS_CM_SHEET_COLORS,
  MANAGE_STATUS_CM_SHEET_LABELS,
  ManageStatusCmSheet,
  VERIFICATION_STATUS_COLORS,
  VERIFICATION_STATUS_LABELS,
} from "./AppConstants";
import dayjs from "dayjs";
import { CareerManager } from "../types/api/careerManagers.type";
import { CollaboratorHistoryProject } from "../types/api/projects.type";
import { IT_DATE_FORMAT, IT_SHORT_DATE_FORMAT } from "../utils/date/date.utils";
import {
  findCCById,
  findStatusLabelByValue,
  findLabelFromValue,
  findPracticeById,
} from "../utils/orgFunctions.utils";
import { OrgAreaItem, OrgCmsheet, OrgSettings } from "../types/api/org.type";
import { getInitials } from "../utils/collaborators.utils";
import { VerificationStatusButton } from "../components/VerificationStatusButton/VerificationStatusButton";
import { theme } from "./StyledComponents";
import { ConfigCollaborator } from "../types/api/configurations.type";
import { BodyTyp } from "../components/Typography/Typography";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../components/core/Select/Select";
import { InputSizes } from "../components/core/Input/Input";
import { AttachSelectableTechSkill } from "../screens/CollaboratorDetail/CollaborProfileTabs/ProfessionalHistory/Skills/Techniques/components/AttachHistoryTechSkillTable";
import {
  TableCellAlignEnum,
  TableFixedCellAlignEnum,
} from "../components/core/Table/Table";
import { TechSkill } from "../types/api/techSkills.type";

export const dateFormat = (date?: string | null) =>
  date ? dayjs(date).format(IT_DATE_FORMAT) : EMPTY_STRING;

export const shortDateFormat = (date?: string | null) =>
  date ? dayjs(date).format(IT_SHORT_DATE_FORMAT) : EMPTY_STRING;

export const getCollaboratorsTableColumns = (
  collaboratorType: CollaboratorTypeEnum,
  orgArea: OrgAreaItem[],
  orgCm: OrgCmsheet,
  isMobile?: boolean,
) => {
  const allCollaboratorsColumns = [
    {
      key: "image",
      width: theme.variables.table_avatar_cell_width,
      fixed: isMobile ? undefined : TableFixedCellAlignEnum.LEFT,
      render: (data: Collaborator) => {
        return (
          <Avatar
            variant={AvatarDimensionsEnum.SMALL}
            collaboratorId={data.id}
            initials={getInitials(data.name, data.surname)}
          />
        );
      },
    },
    {
      title: SURNAME_LABEL,
      key: "surname",
      width: theme.variables.table_surname_cell_width,
      fixed: isMobile ? undefined : TableFixedCellAlignEnum.LEFT,
    },
    {
      title: NAME_LABEL,
      key: "name",
      width: theme.variables.table_name_cell_width,
      fixed: isMobile ? undefined : TableFixedCellAlignEnum.LEFT,
    },
    ...(collaboratorType === CollaboratorTypeEnum.ACTIVE
      ? [
          {
            title: PERFORMANCE_COACH_LABEL,
            key: "careerManagerFullName",
          },
        ]
      : []),
    {
      title: PRACTICE_LABEL,
      key: "practice",
      render: (data: Collaborator) => {
        const practiceObj = findPracticeById(orgArea, data.practice);

        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {practiceObj?.name ?? EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: CC_LABEL,
      key: "cc",
      render: (data: Collaborator) => {
        const ccObj = findCCById(orgArea, data.cc);

        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {ccObj?.name ?? EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: FORM_STATUS_CM_LABEL,
      key: "cmSheetStatus",
      render: (data: Collaborator) => {
        const cmsheetLabel = findStatusLabelByValue(orgCm, data.cmSheetStatus);

        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {cmsheetLabel ?? EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: VERIFICATION_STATUS_LABEL,
      dataIndex: "verificationStatus",
      key: "verificationStatus",
      render: (data: Collaborator) => {
        const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
        const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];

        return data.verificationStatus ? (
          <VerificationStatusButton label={label} color={color} />
        ) : (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
  ];
  return isMobile
    ? allCollaboratorsColumns.filter(
        (col) =>
          col.key === "image" || col.key == "surname" || col.key == "name",
      )
    : allCollaboratorsColumns;
};

export const getCmFollowedTableColumns = (
  collaboratorType: CollaboratorTypeEnum,
  orgArea: OrgAreaItem[],
  orgCm: OrgCmsheet,
) => [
  {
    key: "image",
    width: theme.variables.table_avatar_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
    render: (data: Collaborator) => {
      return (
        <Avatar
          variant={AvatarDimensionsEnum.SMALL}
          collaboratorId={data.id}
          initials={getInitials(data.name, data.surname)}
        />
      );
    },
  },
  {
    title: SURNAME_LABEL,
    key: "surname",
    width: theme.variables.table_surname_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
  },
  {
    title: NAME_LABEL,
    key: "name",
    width: theme.variables.table_name_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
  },
  {
    title: PRACTICE_LABEL,
    key: "practice",
    render: (data: Collaborator) => {
      const practiceObj = findPracticeById(orgArea, data.practice);

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {practiceObj?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: CC_LABEL,
    key: "cc",
    render: (data: Collaborator) => {
      const ccObj = findCCById(orgArea, data.cc);

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {ccObj?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: FORM_STATUS_CM_LABEL,
    key: "cmSheetStatus",
    render: (data: Collaborator) => {
      const cmsheetLabel = findStatusLabelByValue(orgCm, data.cmSheetStatus);

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {cmsheetLabel ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
];

export const techSkillsTableColumns = (isMobile?: boolean) => {
  const allTechSkillsColumns = [
    {
      title: CATEGORY_LABEL,
      key: "competence",
      width: 350,
      getCellText: (data: TechSkill) =>
        data.competence ? data.competence.toUpperCase() : EMPTY_STRING,
      render: (data: TechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.competence ? data.competence.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: SCOPE_LABEL,
      key: "stream",
      getCellText: (data: TechSkill) =>
        data.stream ? data.stream.toUpperCase() : EMPTY_STRING,
      render: (data: TechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.stream ? data.stream.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: CORE_COMPETENCE_LABEL,
      key: "software",
      getCellText: (data: TechSkill) =>
        data.software ? data.software.toUpperCase() : EMPTY_STRING,
      render: (data: TechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.software ? data.software.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: SUB_COMPETENCE_LABEL,
      key: "component",
      getCellText: (data: TechSkill) =>
        data.component ? data.component.toUpperCase() : EMPTY_STRING,
      render: (data: TechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.component ? data.component.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
  ];

  return isMobile
    ? allTechSkillsColumns.filter((col) => col.key === "competence")
    : allTechSkillsColumns;
};

export const getCoursesTableColumns = (
  orgSettings: OrgSettings,
  isMobile?: boolean,
) => {
  const allCoursesColumns = [
    {
      title: NAME_LABEL,
      key: "name",
    },
    {
      title: DURATION_LABEL,
      key: "duration",
    },
    {
      title: TYPOLOGY_LABEL,
      key: "typology",
      render: (data: Course) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {findLabelFromValue(orgSettings.courses.typologies, data.typology)}
          </BodyTyp>
        );
      },
    },
    {
      title: CATEGORY_LABEL,
      key: "category",
      render: (data: Course) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {findLabelFromValue(orgSettings.courses.categories, data.category)}
          </BodyTyp>
        );
      },
    },
  ];
  return isMobile
    ? allCoursesColumns.filter((col) => col.key == "name")
    : allCoursesColumns;
};

export const getCertificationsTableColumns = (
  orgSettings: OrgSettings,
  isMobile?: boolean,
) => {
  const allCertificationColumns = [
    {
      title: NAME_LABEL,
      key: "name",
    },
    {
      title: VALIDITY_LABEL,
      key: "validityPeriod",
    },
    {
      title: CATEGORY_LABEL,
      key: "category",
      render: (data: Certification) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {findLabelFromValue(
              orgSettings.certifications.categories,
              data.category,
            )}
          </BodyTyp>
        );
      },
    },
  ];
  return isMobile
    ? allCertificationColumns.filter((col) => col.key == "name")
    : allCertificationColumns;
};

export const getCollaboratorCoursesPageColumns = (
  onShowCertificate: (record: CollaboratorCmSheetCourse) => void,
  isMyProfile: boolean,
  orgSettings: OrgSettings,
  onUploadCertificate?: (record: CollaboratorCmSheetCourse) => void,
  onToggle?: (record: CollaboratorCmSheetCourse) => void,
) => [
  {
    title: NAME_LABEL,
    key: "name",
    render: (data: CollaboratorCmSheetCourse) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.course.name}
        </BodyTyp>
      );
    },
  },
  {
    title: DURATION_LABEL,
    key: "duration",
    render: (data: CollaboratorCmSheetCourse) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.course.duration ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: TYPOLOGY_LABEL,
    key: "typology",
    render: (data: CollaboratorCmSheetCourse) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {findLabelFromValue(
            orgSettings.courses.typologies,
            data.course.typology,
          )}
        </BodyTyp>
      );
    },
  },
  {
    title: CATEGORY_LABEL,
    key: "category",
    render: (data: CollaboratorCmSheetCourse) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {findLabelFromValue(
            orgSettings.courses.categories,
            data.course.category,
          )}
        </BodyTyp>
      );
    },
  },
  {
    title: isMyProfile ? STATE_LABEL : COMPLETED_LABEL,
    key: "courseToggle",
    render: (data: CollaboratorCmSheetCourse) => {
      if (isMyProfile) {
        return data.isFinished ? COMPLETED_LABEL : IN_PROGRESS_LABEL;
      }
      return (
        <ToggleSwitch
          value={data.isFinished}
          disabled={!onToggle}
          {...(onToggle && { onChange: () => onToggle(data) })}
        />
      );
    },
  },
  ...(isMyProfile
    ? []
    : [
        {
          key: "courseCertificate",
          fixed: TableFixedCellAlignEnum.RIGHT,
          width: theme.variables.table_icon_cell_width,
          render: (data: CollaboratorCmSheetCourse) =>
            data.fileName && data.fileName !== "" ? (
              <IconManager
                size={IconSizeEnum.MEDIUM}
                onClick={() => {
                  onShowCertificate(data);
                }}
              >
                <CertificateIcon />
              </IconManager>
            ) : onUploadCertificate ? (
              <IconManager
                size={IconSizeEnum.MEDIUM}
                onClick={() => {
                  onUploadCertificate(data);
                }}
              >
                <UploadIcon />
              </IconManager>
            ) : null,
        },
      ]),
];

export const getCollaboratorCoursesModalColumns = ({
  orgSettings,
}: {
  orgSettings: OrgSettings;
}) => [
  {
    title: NAME_LABEL,
    key: "name",
  },
  {
    title: DURATION_LABEL,
    key: "duration",
  },
  {
    title: TYPOLOGY_LABEL,
    key: "typology",
    render: (data: Course) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {findLabelFromValue(orgSettings.courses.typologies, data.typology)}
        </BodyTyp>
      );
    },
  },
  {
    title: CATEGORY_LABEL,
    key: "category",
    render: (data: Course) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {findLabelFromValue(orgSettings.courses.categories, data.category)}
        </BodyTyp>
      );
    },
  },
];

export const getCollaboratorCertificationsPageColumns = (
  onView: (record: CollaboratorCmSheetCertification) => void,
  isMyProfile: boolean,
  orgSettings: OrgSettings,
) => [
  {
    title: NAME_LABEL,
    key: "name",
    render: (data: CollaboratorCmSheetCertification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.certification.name}
        </BodyTyp>
      );
    },
  },
  {
    title: DATE_ACHIEVEMENT_LABEL,
    key: "deliveryDate",
    render: (data: CollaboratorCmSheetCertification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {dateFormat(data.deliveryDate)}
        </BodyTyp>
      );
    },
  },
  {
    title: VALIDITY_EXPIRATION_LABEL,
    key: "expirationDate",
    render: (data: CollaboratorCmSheetCertification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {dateFormat(data.expirationDate)}
        </BodyTyp>
      );
    },
  },
  {
    title: STATE_LABEL,
    key: "status",
    render: (data: CollaboratorCmSheetCertification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.status
            ? findLabelFromValue(orgSettings.certifications.status, data.status)
            : EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  ...(isMyProfile
    ? []
    : [
        {
          key: "fileName",
          fixed: TableFixedCellAlignEnum.RIGHT,
          width: theme.variables.table_icon_cell_width,
          render: (data: CollaboratorCmSheetCertification) =>
            data.fileName && data.fileName !== "" ? (
              <IconManager
                size={IconSizeEnum.MEDIUM}
                onClick={() => onView(data)}
              >
                <CertificateIcon />
              </IconManager>
            ) : null,
        },
      ]),
];

export const getCollaboratorCertificationsModalColumns = (
  orgSettings: OrgSettings,
) => [
  {
    title: NAME_LABEL,
    key: "name",
  },
  {
    title: VALIDITY_LABEL,
    key: "validityPeriod",
  },
  {
    title: CATEGORY_LABEL,
    key: "category",
    render: (data: Certification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {findLabelFromValue(
            orgSettings.certifications.categories,
            data.category,
          )}
        </BodyTyp>
      );
    },
  },
];

export const careerManagerColumns = (
  org: OrgAreaItem[],
  isMobile?: boolean,
) => {
  const allCarrerManagerColumns = [
    {
      key: "image",
      width: theme.variables.table_avatar_cell_width,
      fixed: isMobile ? undefined : TableFixedCellAlignEnum.LEFT,
      render: (data: CareerManager) => {
        return (
          <Avatar
            variant={AvatarDimensionsEnum.SMALL}
            collaboratorId={data.id}
            initials={getInitials(data.name, data.surname)}
          />
        );
      },
    },
    {
      title: SURNAME_LABEL,
      key: "surname",
      width: theme.variables.table_surname_cell_width,
      fixed: isMobile ? undefined : TableFixedCellAlignEnum.LEFT,
    },
    {
      title: NAME_LABEL,
      key: "name",
      width: theme.variables.table_surname_cell_width,
      fixed: isMobile ? undefined : TableFixedCellAlignEnum.LEFT,
    },
    {
      title: INTERNAL_GRADE_LABEL,
      key: "internalGrade",
    },
    {
      title: PRACTICE_LABEL,
      key: "practice",
      render: (data: CareerManager) => {
        const practiceObj = findPracticeById(org, data.practice);
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {practiceObj?.name ?? EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: CC_LABEL,
      key: "cc",
      render: (data: CareerManager) => {
        const ccObj = findCCById(org, data.cc);

        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {ccObj?.name ?? EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
  ];
  return isMobile
    ? allCarrerManagerColumns.filter(
        (col) =>
          col.key === "image" || col.key == "surname" || col.key == "name",
      )
    : allCarrerManagerColumns;
};

export const collaboratorCmColumns = [
  {
    key: "image",
    width: theme.variables.table_avatar_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
    render: (data: CollaboratorCM) => {
      return (
        <Avatar
          variant={AvatarDimensionsEnum.SMALL}
          collaboratorId={data.id}
          initials={getInitials(data.name, data.surname)}
        />
      );
    },
  },
  {
    title: PERFORMANCE_COACH_LABEL,
    key: "name",
    render: (data: CollaboratorCM) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {`${data.surname} ${data.name}`}
        </BodyTyp>
      );
    },
  },
  {
    title: STARTING_DATE_LABEL,
    key: "startDate",
    render: (data: CollaboratorCM) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.startDate
            ? dayjs(data.startDate).format(IT_DATE_FORMAT)
            : EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: ENDING_DATE_LABEL,
    key: "endDate",
    render: (data: CollaboratorCM) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.endDate
            ? dayjs(data.endDate).format(IT_DATE_FORMAT)
            : EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
];

export const getCollaboratorHistoryCoursesColumns = (
  onView: (record: CollaboratorHistoryCourse) => void,
) => [
  {
    title: NAME_LABEL,
    key: "name",
    render: (data: CollaboratorHistoryCourse) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.course.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: ATTENDANCE_YEAR_LABEL,
    key: "attendanceYear",
  },
  {
    title: VERIFICATION_STATUS_LABEL,
    dataIndex: "verificationStatus",
    key: "verificationStatus",
    render: (data: CollaboratorHistoryCourse) => {
      const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
      const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];
      return data.verificationStatus ? (
        <VerificationStatusButton label={label} color={color} />
      ) : (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    key: "fileName",
    width: theme.variables.table_icon_cell_width,
    fixed: TableFixedCellAlignEnum.RIGHT,
    render: (data: CollaboratorHistoryCourse) => {
      return (
        data.fileName && (
          <IconManager
            size={IconSizeEnum.MEDIUM}
            onClick={() => {
              onView(data);
            }}
          >
            <CertificateIcon />
          </IconManager>
        )
      );
    },
  },
];

export const getCollaboratorHistoryCertificationsColumns = (
  onView: (record: CollaboratorHistoryCertification) => void,
  orgSettings: OrgSettings,
) => [
  {
    title: NAME_LABEL,
    key: "name",
    render: (data: CollaboratorHistoryCertification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.certification.name}
        </BodyTyp>
      );
    },
  },
  {
    title: DATE_ACHIEVEMENT_LABEL,
    key: "deliveryDate",
    render: (data: CollaboratorHistoryCertification) => {
      const dateFormat =
        data.deliveryDate !== null
          ? dayjs(data.deliveryDate).format(IT_DATE_FORMAT)
          : EMPTY_STRING;

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {dateFormat}
        </BodyTyp>
      );
    },
  },
  {
    title: VALIDITY_EXPIRATION_LABEL,
    key: "expirationDate",
    render: (data: CollaboratorHistoryCertification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {dateFormat(data.expirationDate) ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: STATE_LABEL,
    key: "status",
    render: (data: CollaboratorHistoryCertification) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {findLabelFromValue(orgSettings.certifications.status, data.status)}
        </BodyTyp>
      );
    },
  },
  {
    title: VERIFICATION_STATUS_LABEL,
    dataIndex: "verificationStatus",
    key: "verificationStatus",
    render: (data: CollaboratorHistoryCertification) => {
      const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
      const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];
      return data.verificationStatus ? (
        <VerificationStatusButton label={label} color={color} />
      ) : (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    key: "fileName",
    width: theme.variables.table_icon_cell_width,
    fixed: TableFixedCellAlignEnum.RIGHT,
    render: (data: CollaboratorHistoryCertification) => {
      return (
        data.fileName && (
          <IconManager
            size={IconSizeEnum.MEDIUM}
            onClick={() => {
              onView(data);
            }}
          >
            <CertificateIcon />
          </IconManager>
        )
      );
    },
  },
];

export const getCollaboratorProjectsColumns = [
  {
    title: CORPORATION_LABEL,
    key: "historyProfessionalExperience",
    render: (data: CollaboratorHistoryProject) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.historyProfessionalExperience.companyName ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: ROLE_LABEL,
    key: "projectRole",
    render: (data: CollaboratorHistoryProject) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.projectItem.projectRole ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: NAME_LABEL,
    key: "name",
    render: (data: CollaboratorHistoryProject) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.projectItem.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: STARTING_DATE_LABEL,
    key: "startDate",
    render: (data: CollaboratorHistoryProject) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.projectItem.startDate ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: ENDING_DATE_LABEL,
    key: "endDate",
    render: (data: CollaboratorHistoryProject) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.projectItem.endDate ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: VERIFICATION_STATUS_LABEL,
    dataIndex: "verificationStatus",
    key: "verificationStatus",
    render: (data: CollaboratorHistoryProject) => {
      const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
      const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];
      return data.verificationStatus ? (
        <VerificationStatusButton label={label} color={color} />
      ) : (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
];

export const getConfigActiveResourcesColumns = (orgSettings: OrgSettings) => [
  {
    key: "image",
    width: theme.variables.table_avatar_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
    render: (data: ConfigCollaborator) => {
      return (
        <Avatar
          variant={AvatarDimensionsEnum.SMALL}
          collaboratorId={data.id}
          initials={getInitials(data.firstName, data.lastName)}
        />
      );
    },
  },
  {
    title: SURNAME_LABEL,
    key: "lastName",
    fixed: TableFixedCellAlignEnum.LEFT,
    width: theme.variables.table_surname_cell_width,
  },
  {
    title: NAME_LABEL,
    key: "firstName",
    fixed: TableFixedCellAlignEnum.LEFT,
    width: theme.variables.table_name_cell_width,
  },
  {
    title: EMAIL_LABEL,
    key: "email",
  },
  {
    title: PRACTICE_LABEL,
    key: "practiceId",
    render: (data: ConfigCollaborator) => {
      const practice = findPracticeById(
        orgSettings.areaList.all,
        data.practiceId,
        data.areaId,
      );

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {practice?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: CC_LABEL,
    key: "ccID",
    render: (data: ConfigCollaborator) => {
      const cc = findCCById(
        orgSettings.areaList.all,
        data.ccId,
        data.areaId,
        data.practiceId,
      );

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {cc?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: ROLE_LABEL,
    key: "role",
    render: (data: ConfigCollaborator) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {findLabelFromValue(orgSettings.userRoles.role, data.role)}
        </BodyTyp>
      );
    },
  },
  {
    title: CONTRACT_TYPE,
    key: "contractType",
    render: (data: ConfigCollaborator) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {data.contractType ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: CM_SHEET_LABEL,
    key: "cmSheetStatus",
    align: TableCellAlignEnum.CENTER,
    width: 80,
    render: (data: ConfigCollaborator) => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <IconManager
          size={IconSizeEnum.MEDIUM}
          color={data.enabledCmSheet ? theme.colors.green : theme.colors.red_1}
        >
          {data.enabledCmSheet ? <CheckIcon /> : <CloseIcon />}
        </IconManager>
      </div>
    ),
  },
];

export const getCollaboratorsToArchiveColumns = (orgSettings: OrgSettings) => [
  {
    title: SURNAME_LABEL,
    key: "lastName",
  },
  {
    title: NAME_LABEL,
    key: "firstName",
  },
  {
    title: PRACTICE_LABEL,
    key: "practiceId",
    render: (data: ConfigCollaborator) => {
      const practice = findPracticeById(
        orgSettings.areaList.all,
        data.practiceId,
        data.areaId,
      );

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {practice?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: CC_LABEL,
    key: "ccID",
    render: (data: ConfigCollaborator) => {
      const cc = findCCById(
        orgSettings.areaList.all,
        data.ccId,
        data.areaId,
        data.practiceId,
      );

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {cc?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
];

export const getCollaboratorHistorySkillsLanguageColumns = (
  orgSettings: OrgSettings,
) => {
  return [
    {
      title: LANGUAGE_LABEL,
      dataIndex: "language",
      key: "language",
      render: (data: CollaboratorsHistorySkillLanguages) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {findLabelFromValue(
              orgSettings.languageSkills.languages,
              data.language,
            )}
          </BodyTyp>
        );
      },
    },
    {
      title: COMPREHENSION_LABEL,
      dataIndex: "listeningComprehension",
      key: "listeningComprehension",
      render: (data: CollaboratorsHistorySkillLanguages) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {`${LISTENING_LABEL}: ${data.listeningComprehension} | ${READING_LABEL}: ${data.readingComprehension}`}
          </BodyTyp>
        );
      },
    },
    {
      title: SPOKEN_LABEL,
      dataIndex: "spokenInteraction",
      key: "spokenInteraction",
      render: (data: CollaboratorsHistorySkillLanguages) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {`${INTERACTION_LABEL}: ${data.spokenInteraction} | ${SPOKEN_PRODUCTION}: ${data.spokenOralProduction}`}
          </BodyTyp>
        );
      },
    },
    {
      title: WRITTEN_LABEL,
      dataIndex: "written",
      key: "written",
      width: 68,
      render: (data: CollaboratorsHistorySkillLanguages) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.written}
          </BodyTyp>
        );
      },
    },
    {
      title: VERIFICATION_STATUS_LABEL,
      dataIndex: "verificationStatus",
      key: "verificationStatus",
      render: (data: CollaboratorsHistorySkillLanguages) => {
        const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
        const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];
        return data.verificationStatus ? (
          <VerificationStatusButton label={label} color={color} />
        ) : (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
  ];
};

export const getArchivingPendingTableColumns = (
  orgArea: OrgAreaItem[],
  orgCm: OrgCmsheet,
) => [
  {
    key: "image",
    width: theme.variables.table_avatar_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
    render: (data: ConfigCollaborator) => {
      return (
        <Avatar
          variant={AvatarDimensionsEnum.SMALL}
          collaboratorId={data.id}
          initials={getInitials(data.firstName, data.lastName)}
        />
      );
    },
  },
  {
    title: SURNAME_LABEL,
    fixed: TableFixedCellAlignEnum.LEFT,
    width: theme.variables.table_surname_cell_width,
    key: "lastName",
  },
  {
    title: NAME_LABEL,
    fixed: TableFixedCellAlignEnum.LEFT,
    width: theme.variables.table_name_cell_width,
    key: "firstName",
  },
  {
    title: PRACTICE_LABEL,
    key: "practice",
    render: (data: ConfigCollaborator) => {
      const practiceObj = findPracticeById(orgArea, data.practiceId);
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {practiceObj?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: CC_LABEL,
    key: "cc",
    render: (data: ConfigCollaborator) => {
      const ccObj = findCCById(orgArea, data.ccId);

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {ccObj?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: PC_ABBREVIATION_LABEL,
    key: "careerManagerFullName",
  },
  {
    title: FORM_STATUS_LABEL,
    key: "cmSheetStatus",
    render: (data: ConfigCollaborator) => {
      const cmsheetLabel = findStatusLabelByValue(orgCm, data.statusCmSheet);

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {cmsheetLabel ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
];

export const getArchivingReadySendSheet = (orgArea: OrgAreaItem[]) => [
  {
    key: "image",
    width: theme.variables.table_avatar_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
    render: (data: ConfigCollaborator) => {
      return (
        <Avatar
          variant={AvatarDimensionsEnum.SMALL}
          collaboratorId={data.id}
          initials={getInitials(data.firstName, data.lastName)}
        />
      );
    },
  },
  {
    title: SURNAME_LABEL,
    key: "lastName",
    width: theme.variables.table_surname_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
  },
  {
    title: NAME_LABEL,
    key: "firstName",
    width: theme.variables.table_name_cell_width,
    fixed: TableFixedCellAlignEnum.LEFT,
  },
  {
    title: PRACTICE_LABEL,
    key: "practice",
    render: (data: ConfigCollaborator) => {
      const practiceObj = findPracticeById(orgArea, data.practiceId);

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {practiceObj?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: CC_LABEL,
    key: "cc",
    render: (data: ConfigCollaborator) => {
      const ccObj = findCCById(orgArea, data.ccId);

      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {ccObj?.name ?? EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
  {
    title: PC_ABBREVIATION_LABEL,
    key: "careerManagerFullName",
  },
  {
    title: VERIFICATION_STATUS_LABEL,
    dataIndex: "verificationStatus",
    key: "verificationStatus",
    render: (data: ConfigCollaborator) => {
      const verificationStatus = data.validCmSheet
        ? MANAGE_STATUS_CM_SHEET_LABELS[ManageStatusCmSheet.READY_TO_SEND]
        : MANAGE_STATUS_CM_SHEET_LABELS[ManageStatusCmSheet.TO_VERIFY];
      const color = data.validCmSheet
        ? MANAGE_STATUS_CM_SHEET_COLORS[ManageStatusCmSheet.READY_TO_SEND]
        : MANAGE_STATUS_CM_SHEET_COLORS[ManageStatusCmSheet.TO_VERIFY];
      return (
        <VerificationStatusButton label={verificationStatus} color={color} />
      );
    },
  },
];

export const getCollaboratorHistoryTechSkillsColumns = (
  orgSettings: OrgSettings,
  showVerificationStatus?: boolean,
) => {
  return [
    {
      title: CATEGORY_LABEL,
      dataIndex: "competence",
      key: "competence",
      render: (data: CollaboratorsHistoryTechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.competence ? data.competence.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },

    {
      title: SCOPE_LABEL,
      dataIndex: "stream",
      key: "stream",
      render: (data: CollaboratorsHistoryTechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.stream ? data.stream.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: CORE_COMPETENCE_LABEL,
      dataIndex: "software",
      key: "software",
      render: (data: CollaboratorsHistoryTechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.software ? data.software.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: SUB_COMPETENCE_LABEL,
      dataIndex: "component",
      key: "component",
      render: (data: CollaboratorsHistoryTechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {data.component ? data.component.toUpperCase() : EMPTY_STRING}
          </BodyTyp>
        );
      },
    },
    {
      title: LEVEL_LABEL,
      dataIndex: "level",
      key: "level",
      render: (data: CollaboratorsHistoryTechSkill) => {
        return (
          <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
            {findLabelFromValue(orgSettings.techSkills.levels, data.level)}
          </BodyTyp>
        );
      },
    },
    ...(showVerificationStatus
      ? [
          {
            title: VERIFICATION_STATUS_LABEL,
            dataIndex: "verificationStatus",
            key: "verificationStatus",

            render: (data: CollaboratorsHistoryTechSkill) => {
              const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
              const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];
              return data.verificationStatus ? (
                <VerificationStatusButton label={label} color={color} />
              ) : (
                <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
                  {EMPTY_STRING}
                </BodyTyp>
              );
            },
          },
        ]
      : []),
  ];
};

export const getAttachTechSkillColumns = (
  showLevel?: boolean,
  onCheckbox?: (record: AttachSelectableTechSkill, checked: boolean) => void,
  selectedRows?: AttachSelectableTechSkill[],
  levelOptions?: SelectOption<string>[],
  onChangeLevel?: (value: string | null, id: number) => void,
  isMobile?: boolean,
) => {
  return [
    ...techSkillsTableColumns(isMobile ?? false),
    ...(showLevel
      ? [
          {
            title: LEVEL_LABEL,
            dataIndex: "level",
            key: "level",
          },
        ]
      : selectedRows && selectedRows.length > 0 && onCheckbox
        ? [
            {
              title: LEVEL_LABEL,
              dataIndex: "level",
              key: "level",
              fixed: TableFixedCellAlignEnum.RIGHT,
              render: (data: AttachSelectableTechSkill) => {
                const isSelected = selectedRows?.some(
                  (row) => row.id === data.id,
                );
                const skill = selectedRows?.find((s) => s.id === data.id);

                if (!isSelected) {
                  return null;
                }

                return isSelected ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select
                      variant={SelectVariantEnum.DEFAULT}
                      size={InputSizes.SMALL}
                      options={levelOptions || []}
                      value={skill?.level || ""}
                      onChange={(value) => {
                        onChangeLevel?.(value, data.id);
                      }}
                      allowClear={false}
                    />
                  </div>
                ) : (
                  []
                );
              },
            },
          ]
        : []),
  ];
};

export const collaboratorHistoryExperienceColumns = [
  { title: COMPANY_NAME_LABEL, key: "companyName" },
  {
    title: STARTING_DATE_LABEL,
    key: "startDate",
    render: (data: CollaboratorHistoryProfessionalExperience) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {shortDateFormat(data.startDate)}
        </BodyTyp>
      );
    },
  },
  {
    title: ENDING_DATE_LABEL,
    key: "endDate",
    render: (data: CollaboratorHistoryProfessionalExperience) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {shortDateFormat(data.endDate)}
        </BodyTyp>
      );
    },
  },
  {
    title: VERIFICATION_STATUS_LABEL,
    dataIndex: "verificationStatus",
    key: "verificationStatus",
    render: (data: CollaboratorHistoryProfessionalExperience) => {
      const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
      const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];
      return data.verificationStatus ? (
        <VerificationStatusButton label={label} color={color} />
      ) : (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
];

export const getCollaboratorHistoryEducationsColumns = () => [
  {
    title: EDUCATIONAL_QUALIFICATION_LABEL,
    key: "qualification",
  },
  {
    title: EDUCATIONAL_LABEL,
    key: "institution",
  },
  {
    title: STUDY_PATH_LABEL,
    key: "studyPath",
  },
  {
    title: YEAR_START_LABEL,
    key: "startDate",
    render: (data: CollaboratorHistoryEducation) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {shortDateFormat(data.startDate)}
        </BodyTyp>
      );
    },
  },
  {
    title: YEAR_END_LABEL,
    key: "endDate",
    render: (data: CollaboratorHistoryEducation) => {
      return (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {shortDateFormat(data.endDate)}
        </BodyTyp>
      );
    },
  },
  {
    title: VERIFICATION_STATUS_LABEL,
    dataIndex: "verificationStatus",
    key: "verificationStatus",
    render: (data: CollaboratorHistoryEducation) => {
      const label = VERIFICATION_STATUS_LABELS[data.verificationStatus];
      const color = VERIFICATION_STATUS_COLORS[data.verificationStatus];
      return data.verificationStatus ? (
        <VerificationStatusButton label={label} color={color} />
      ) : (
        <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
          {EMPTY_STRING}
        </BodyTyp>
      );
    },
  },
];
